import { useEffect, useState } from "react";
import { Avatar, Box, Button, Center, Flex, Loader, Text, Title } from "@mantine/core";
import { IconLogout } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc, { removeToken } from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import LogoutModal from "./LogoutModal";

type AdminProfile = {
  id: string;
  username: string;
};

export default function ProfilePage() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoutOpened, setLogoutOpened] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await trpc.admin.getCurrentAdmin.query();
        setProfile({ id: res.data.id, username: res.data.username });
      } catch (err) {
        handleTrpcError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleLogout = () => {
    removeToken();
    navigate("/login", { replace: true });
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  const initial = profile?.username?.charAt(0).toUpperCase() ?? "A";

  return (
    <Flex direction="column" w="100%" p="xl">
      {/* ── Header ── */}
      <Flex justify="space-between" align="center" mb="xl">
        <Title order={2} fw={700}>
          Profile
        </Title>
        <Button color="primary" leftSection={<IconLogout size={16} />} onClick={() => setLogoutOpened(true)}>
          Log Out
        </Button>
      </Flex>

      {/* ── Admin info ── */}
      <Flex align="center" gap="md" mb="xl">
        <Avatar size={48} radius="xl" color="pink" variant="filled">
          {initial}
        </Avatar>
        <Text fw={700} fz="lg">
          {profile?.username ?? "—"}
        </Text>
      </Flex>

      {/* ── Stats row ── */}
      <Flex gap="md">
        <Box
          flex={1}
          px="lg"
          py="md"
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Flex justify="space-between" align="center">
            <Text fz="sm" c="slate.5">Total Invested</Text>
            <Text fw={600} fz="sm">—</Text>
          </Flex>
        </Box>
        <Box
          flex={1}
          px="lg"
          py="md"
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Flex justify="space-between" align="center">
            <Text fz="sm" c="slate.5">Current value of investments</Text>
            <Text fw={600} fz="sm">—</Text>
          </Flex>
        </Box>
        <Box
          flex={1}
          px="lg"
          py="md"
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Flex justify="space-between" align="center">
            <Text fz="sm" c="slate.5">XIRR</Text>
            <Text fw={600} fz="sm">—</Text>
          </Flex>
        </Box>
      </Flex>

      <LogoutModal
        opened={logoutOpened}
        onClose={() => setLogoutOpened(false)}
        onConfirm={handleLogout}
      />
    </Flex>
  );
}
